import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * AnomalyBanner — thin always-on stripe above the dashboard listing live
 * anomalies scoped to the caller. Collapsed by default; expand to see detail.
 * Auto-refreshes every 3 minutes. Silent-hides if total=0.
 */
const SEV_BG = { critical: '#FEE2E2', high: '#FEF3E5', warn: '#FEF9C3', info: '#F7F5F2' };
const SEV_FG = { critical: '#991B1B', high: '#9A3412', warn: '#854D0E', info: '#6B7280' };
const SEV_DOT = { critical: '#B91C1C', high: '#C98A1F', warn: '#CA8A04', info: '#9CA3AF' };

export default function AnomalyBanner({ className = '' }) {
  const [snap, setSnap] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const r = await api.get('/mira-intel/anomalies'); setSnap(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 180000);
    return () => clearInterval(iv);
  }, []);

  if (!snap || (snap.total || 0) === 0) return null;

  const top = snap.top || [];
  const worst = top[0]?.severity || 'info';

  return (
    <div className={`rounded-lg border ${className}`}
         style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}
         data-testid="anomaly-banner">
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F7F5F2] rounded-lg"
              data-testid="anomaly-banner-toggle">
        <span className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: SEV_DOT[worst] }} />
        <AlertTriangle size={12} style={{ color: SEV_FG[worst] }} />
        <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
          {snap.total} active signal{snap.total > 1 ? 's' : ''}
        </span>
        <span className="text-[10px]" style={{ color: '#6B7280' }}>
          {Object.entries(snap.totals || {}).filter(([, n]) => n).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(' · ')}
        </span>
        <span className="ml-auto text-[10px] flex items-center gap-0.5" style={{ color: '#9CA3AF' }}>
          {open ? 'hide' : 'show'}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </button>
      {open && (
        <div className="border-t px-3 py-2 space-y-1.5" style={{ borderColor: '#F3F0EB' }}>
          {top.map((a, i) => (
            <div key={i}
                 className="flex items-start gap-2 rounded-md px-2 py-1.5"
                 style={{ backgroundColor: SEV_BG[a.severity] || '#F7F5F2' }}
                 data-testid={`anomaly-item-${i}`}>
              <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: SEV_DOT[a.severity] }} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold truncate" style={{ color: SEV_FG[a.severity] }}>
                  {a.title}
                </p>
                <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>{a.detail}</p>
              </div>
              {a.cta && (
                <span className="text-[10px] hidden md:block italic" style={{ color: '#9CA3AF' }}>
                  → {a.cta}
                </span>
              )}
            </div>
          ))}
          {snap.total > top.length && (
            <p className="text-[10px] text-center pt-1" style={{ color: '#9CA3AF' }}>
              showing top {top.length} of {snap.total} — ask Mira for the rest
            </p>
          )}
        </div>
      )}
    </div>
  );
}
